import { useEffect, useRef, useState } from "react";

import { StatusToast } from "./status-toast";
import { fetchBuild, isOutdated, VERSION_POLL_MS } from "./version-watch";

/** Watch the served build and report when this page has fallen behind it.
 *
 *  The first answer is the baseline, not a verdict: it records what this page is
 *  running, and only a later, different answer means anything. Once it is true
 *  it stays true and the polling stops — the page cannot become current again
 *  without the reload the banner is asking for.
 */
export function useUpdateAvailable(pollMs = VERSION_POLL_MS): boolean {
  const loaded = useRef<string | null>(null);
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    if (available) return;
    let active = true;

    const check = async () => {
      const build = await fetchBuild();
      if (!active || build === null) return;
      if (loaded.current === null) {
        loaded.current = build;
        return;
      }
      if (isOutdated(loaded.current, build)) setAvailable(true);
    };

    void check();
    const timer = window.setInterval(() => void check(), pollMs);
    // The case this is really for: a tab open since Tuesday. Polling alone
    // would find the deploy eventually; checking on return finds it before the
    // reader clicks anything.
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      active = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [available, pollMs]);

  return available;
}

/** The notice itself.
 *
 *  Built on the same `StatusToast` the control room already uses for notices
 *  that carry an action, rather than a full-width bar across the top: both apps
 *  have sticky chrome at `top: 0` -- the portal's topbar, the control room's
 *  sidebar -- and a bar over that would cover the navigation it sits on.
 *
 *  Dismissible, deliberately. Reloading discards anything typed and not yet
 *  sent -- a half-written request, an unsaved plan -- so this offers the reload
 *  and lets the reader choose the moment. It is a prompt, not a modal, and the
 *  page underneath keeps working.
 */
export function UpdateBanner({
  available,
  onReload = () => window.location.reload(),
}: {
  available: boolean;
  onReload?: () => void;
}) {
  const [dismissed, setDismissed] = useState(false);
  if (!available || dismissed) return null;

  return (
    <div className="update-toast">
      <StatusToast
        message="A new version of Netra is available."
        persistent
        onDismiss={() => setDismissed(true)}
        action={{ label: "Refresh", onClick: onReload }}
      />
    </div>
  );
}

/** What the app mounts: the watcher and the notice, wired together. */
export function UpdateNotice({ pollMs = VERSION_POLL_MS }: { pollMs?: number }) {
  return <UpdateBanner available={useUpdateAvailable(pollMs)} />;
}
